<script setup lang="ts">
/**
 * ジャッジ待ち申請モーダル。VB.NET版 UncheckPicker.exe(GUIモード)相当。
 * 未審査・BAN状態の商品ASINを複数まとめて入力し、「ジャッジ待ち」キューへ
 * 一括登録する。NGワード・不明カテゴリ・アダルト・サイズ超過等の事前チェックを
 * 通過したものだけが受理される(却下されたものは理由付きで一覧表示される)。
 *
 * 【VB版との違い】VB版はDB未登録の新規ASINについて、Amazonから商品情報を
 * 自動取得する処理を持っていたが、使用していたAPI(旧Product Advertising API)が
 * 現在完全停止済みのため、Web版では対応しない。DB未登録のASINは
 * 「商品情報がDBに未登録のため申請できません」という結果になる。
 */
import { ref, computed } from 'vue'
import { useApplyJudgmentQueueMutation } from '@/api/queries'
import { useConfirmModalStore } from '@/stores/confirmModal'

const props = defineProps<{
  initialAsin?: string | null
  regionCode?: string
}>()

const emit = defineEmits<{
  close: []
  /** 申請を送った(受理・却下の結果が出た)。一覧のチェックを外すために使う */
  applied: []
}>()

const confirmModal = useConfirmModalStore()
const asinText = ref(props.initialAsin ?? '')
const applyMutation = useApplyJudgmentQueueMutation()
const results = ref<{ asin: string; accepted: boolean; message: string }[] | null>(null)

/** 申請が終わった(結果が出た)。この後は「閉じる」だけにする(申請ボタンが残ると、処理できなかったと誤解されるため) */
const done = computed(() => results.value !== null)
const acceptedCount = computed(() => results.value?.filter((r) => r.accepted).length ?? 0)

const parsedAsins = computed(() =>
  asinText.value
    .split(/[\s\n,、]+/)
    .map((s) => s.trim())
    .filter(Boolean),
)

async function onApply() {
  if (done.value || parsedAsins.value.length === 0) return
  if (
    !(await confirmModal.ask(
      `${parsedAsins.value.length}件のASINについて、ジャッジ待ち申請を行います。よろしいですか？`,
    ))
  )
    return

  applyMutation.mutate(
    { asins: parsedAsins.value, regionCode: props.regionCode || 'jp' },
    {
      onSuccess: (res) => {
        results.value = res.results
        emit('applied')
      },
    },
  )
}
</script>

<template>
  <div class="bim-overlay" @click="emit('close')">
    <div class="bim-modal" @click.stop>
      <div class="bim-head">
        <div class="bim-title">ジャッジ待ち申請</div>
        <button class="bim-close" @click="emit('close')">
          <svg class="icon" viewBox="0 0 24 24" style="width:18px;height:18px;"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
      </div>

      <div class="bim-sub">
        ジャッジされていない商品のジャッジ待ち申請ができます。申請が受理されるとジャッジ待ちとなり、
        商品ジャッジの対象となります。ただし、商品がNGワードに該当する場合等は申請が却下されます。
        BAN商品も再申請できます(受理されるとBANが解除されます)。
      </div>

      <div class="field">
        <label>ASIN(複数可。改行・スペース・カンマ区切り)</label>
        <textarea
          v-model="asinText"
          :readonly="done"
          rows="6"
          placeholder="B0XXXXXXXX&#10;B0YYYYYYYY"
          style="width:100%;resize:vertical;"
        ></textarea>
      </div>

      <div v-if="results" class="jq-done">
        申請が完了しました（受理 {{ acceptedCount }}件 ・ 却下 {{ results.length - acceptedCount }}件）
      </div>
      <div v-if="results" class="jq-results">
        <div v-for="(r, idx) in results" :key="idx" class="jq-result-row" :class="{ ok: r.accepted, ng: !r.accepted }">
          <svg v-if="r.accepted" class="icon icon-xs" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          <svg v-else class="icon icon-xs" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
          <span>{{ r.message }}</span>
        </div>
      </div>

      <div class="bim-actions">
        <button :class="done ? 'btn btn-primary' : 'btn btn-ghost'" @click="emit('close')">閉じる</button>
        <button
          v-if="!done"
          class="btn btn-primary"
          :disabled="applyMutation.isPending.value || parsedAsins.length === 0"
          @click="onApply"
        >
          {{ applyMutation.isPending.value ? '申請中…' : `${parsedAsins.length}件をジャッジ待ち申請` }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.jq-done{margin:10px 0 6px;padding:8px 12px;border-radius:8px;background:var(--ok-tint);color:var(--ok);font-weight:700;font-size:13px;}
</style>
